import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Modal,
  Button,
  Container,
  Paper,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TranslateIcon from '@mui/icons-material/Translate';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import languageLearningApi from '../middleware/languageLearningApi';
import api from '../../../api';
import { useParams, useNavigate } from 'react-router-dom';
import { useNavigation } from '../../../context/NavigationContext';

import MultiStepCardViewer from '../../srs/MultiCardStepViewer';

// Styled components with blue/white theme
const SentenceCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  height: '100%',
  backgroundColor: 'white',
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  borderRadius: theme.spacing(2),
  overflow: 'visible',
  display: 'flex',
  flexDirection: 'column',
  borderLeft: '4px solid transparent',
}));

const SentenceCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2.5),
  '&:last-child': {
    paddingBottom: theme.spacing(2.5),
  },
}));

const SentenceCount = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -12,
  left: 16,
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  fontSize: 14,
  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
  zIndex: 1,
}));

const SentenceText = styled(Typography)(({ theme }) => ({
  fontSize: '1.05rem',
  fontWeight: 500,
  lineHeight: 1.4,
  marginBottom: theme.spacing(1.5),
  color: theme.palette.text.primary,
}));

const TranslationText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    fontSize: '0.875rem',
    marginRight: theme.spacing(0.5),
    opacity: 0.7,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: theme.spacing(3),
  fontWeight: 500,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
    backgroundColor: alpha(theme.palette.primary.main, 0.9),
  },
  padding: theme.spacing(0.75, 2),
}));

const ViewToggleButton = styled(IconButton)(({ theme, active }) => ({
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: active 
      ? alpha(theme.palette.primary.main, 0.2) 
      : alpha(theme.palette.primary.main, 0.05),
  },
  borderRadius: theme.spacing(1),
  marginLeft: theme.spacing(1),
}));

const ModalContent = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 800,
  maxHeight: '90vh',
  height: 'auto',
  backgroundColor: 'white',
  boxShadow: theme.shadows[24],
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
}));

const ModalHeader = styled(AppBar)(({ theme }) => ({
  position: 'relative',
  backgroundColor: 'white',
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(6),
  gap: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.primary.main,
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(6),
  gap: theme.spacing(2),
  textAlign: 'center',
  minHeight: 300,
  border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
  borderRadius: theme.spacing(2),
  margin: theme.spacing(3, 0),
  backgroundColor: alpha(theme.palette.primary.main, 0.02),
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: `4px solid transparent`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  transition: 'all 0.2s ease',
  backgroundColor: 'white',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
}));

const ListNumberAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  width: 32,
  height: 32,
  fontSize: '0.875rem',
  fontWeight: 'bold',
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3, 3, 2, 3),
  marginBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '40%',
    height: '100%',
    backgroundImage: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.4) 100%)',
    zIndex: 1,
  },
}));

const CourseletSentencesView = ({ 
  courseletId: propCourseletId,
  hideBackButton = false,
  showAsEmbedded = false 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [sentences, setSentences] = useState([]);
  const [selectedSentence, setSelectedSentence] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingCard, setFetchingCard] = useState(false);
  const [courseDetails, setCourseDetails] = useState({ title: 'Courselet Sentences' });
  const { courseletId: paramCourseletId } = useParams();
  const courseletId = propCourseletId || paramCourseletId;
  const { currentCourse } = useNavigation();
  
  const cardViewerRef = useRef(null);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'

  const containerProps = showAsEmbedded ? {} : { maxWidth: "lg", sx: { py: 3 } };

  useEffect(() => {
    const fetchSentences = async () => {
      try {
        // Optional: Fetch courselet details to show title
        try {
          const courseResponse = await languageLearningApi.get(`/courselets/${courseletId}/details`);
          if (courseResponse?.data?.courselet) {
            setCourseDetails({
              title: courseResponse.data.courselet.title || 'Courselet Sentences',
              language: currentCourse.language || '',
            });
          }
        } catch (error) {
          console.error('Error fetching courselet details:', error);
        }

        // Fetch sentences
        const response = await languageLearningApi.get(`/sentences/${courseletId}/sentences`);
        setSentences(response.data.sentences);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching sentences:', error);
        setLoading(false);
      }
    };

    fetchSentences();
  }, [courseletId]);

  const handleSentenceView = async (sentence) => {
    setSelectedSentence(sentence);
    setFetchingCard(true);
    
    try {
      // Get the cardId from the sentence and fetch the multi-step card data
      const cardId = sentence.cardId;
      if (cardId) {
        const response = await api.get(`/multi-step-card/${cardId}`);
        console.log('Card data from get multi step card api', response);
        setCardData(response.data.card);
      } else {
        console.error('No cardId found for this sentence');
        setCardData(null);
      }
    } catch (error) {
      console.error('Error fetching multi-step card data:', error);
      setCardData(null);
    } finally {
      setFetchingCard(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedSentence(null);
    setCardData(null);
  };

  const toggleViewMode = (mode) => {
    setViewMode(mode);
  };

  const goBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size={40} color="primary" />
        <Typography variant="h6" color="primary">
          Loading sentences...
        </Typography>
      </LoadingContainer>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header Bar */}
      <HeaderContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {!hideBackButton && (
            <IconButton onClick={goBack} sx={{ mr: 1, color: theme.palette.primary.main }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h5" component="h1" fontWeight="600" color={theme.palette.primary.main}>
            {courseDetails.title}
          </Typography>
          
          {courseDetails.language && (
            <Box 
              sx={{ 
                ml: 2, 
                px: 2, 
                py: 0.5, 
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: '20px',
                display: 'inline-flex',
                alignItems: 'center'
              }}
            >
              <TranslateIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.main }} />
              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                {courseDetails.language}
              </Typography>
            </Box>
          )}
        </Box>

        {!showAsEmbedded && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="body1" color="text.secondary">
            {sentences.length} sentences
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              View:
            </Typography>
            <Tooltip title="Card view">
              <ViewToggleButton 
                active={viewMode === 'card'} 
                onClick={() => toggleViewMode('card')}
                size="small"
              >
                <ViewModuleIcon />
              </ViewToggleButton>
            </Tooltip>
            <Tooltip title="List view">
              <ViewToggleButton 
                active={viewMode === 'list'} 
                onClick={() => toggleViewMode('list')}
                size="small"
              >
                <ViewListIcon />
              </ViewToggleButton>
            </Tooltip>
          </Box>
        </Box>
        )}
      </HeaderContainer>

      {/* Sentences View (Card or List) */}
      {sentences.length > 0 ? (
        viewMode === 'card' ? (
          // Card View
          <Grid container spacing={3}>
            {sentences.map((sentence, index) => (
              <Grid item xs={12} sm={6} md={4} key={sentence._id}>
                <SentenceCard elevation={2} onClick={() => handleSentenceView(sentence)}>
                  <SentenceCount>{index + 1}</SentenceCount>
                  <SentenceCardContent>
                    <SentenceText>
                      {sentence.text}
                    </SentenceText>
                    
                    <TranslationText>
                      <TranslateIcon /> {sentence.translation}
                    </TranslationText>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <ActionButton
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleSentenceView(sentence)}
                      >
                        View
                      </ActionButton>
                    </Box>
                  </SentenceCardContent>
                </SentenceCard>
              </Grid>
            ))}
          </Grid>
        ) : (
          // List View
          <List sx={{ width: '100%', bgcolor: 'background.paper', pt: 2 }}>
            {sentences.map((sentence, index) => (
              <StyledListItem
                key={sentence._id}
                onClick={() => handleSentenceView(sentence)}
                button
                alignItems="flex-start"
              >
                <ListNumberAvatar>{index + 1}</ListNumberAvatar>
                <ListItemText
                  primary={sentence.text}
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <TranslateIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.7 }} />
                      {sentence.translation}
                    </Box>
                  }
                  primaryTypographyProps={{ 
                    variant: 'body1', 
                    fontWeight: 500,
                    sx: { mb: 0.5 }
                  }}
                  secondaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary',
                    style: { fontStyle: 'italic' }
                  }}
                  sx={{ ml: 2 }}
                />
                <IconButton edge="end" sx={{ color: theme.palette.primary.main }}>
                  <KeyboardArrowRightIcon />
                </IconButton>
              </StyledListItem>
            ))}
          </List>
        )
      ) : (
        <EmptyState>
          <Typography variant="h6" color="primary">
            No sentences found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This courselet doesn't have any sentences yet.
          </Typography>
        </EmptyState>
      )}

      {/* Sentence Details Modal with MultiStepCardViewer */}
      <Modal
        open={!!selectedSentence}
        onClose={handleCloseModal}
        aria-labelledby="sentence-details-modal"
        aria-describedby="detailed-sentence-information"
      >
        <ModalContent>
          <ModalHeader>
            <Toolbar variant="dense">
              <IconButton edge="start" color="inherit" onClick={handleCloseModal} aria-label="close">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="subtitle1" component="div" sx={{ ml: 2, flex: 1, fontWeight: 500 }}>
                Sentence Details
              </Typography>
            </Toolbar>
          </ModalHeader>
          
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
            {fetchingCard ? (
              <LoadingContainer>
                <CircularProgress size={30} color="primary" />
                <Typography variant="body1">Loading sentence details...</Typography>
              </LoadingContainer>
            ) : selectedSentence && cardData ? (
              <Box>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 2,
                    borderLeft: `4px solid ${theme.palette.primary.main}`
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {selectedSentence.text}
                  </Typography>
                  <Typography variant="body1" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    {selectedSentence.translation}
                  </Typography>
                </Paper>

                <MultiStepCardViewer
                  key={cardData._id || selectedSentence._id}
                  ref={cardViewerRef}
                  cardData={cardData}
                  language={courseDetails?.language}
                />
              </Box>
            ) : selectedSentence && (
              <Box textAlign="center" p={3}>
                <Typography variant="h6" color="error" textAlign="center" mb={2}>
                  Could not load sentence details
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  There was a problem loading the details for this sentence. This might be because no card has been created yet.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCloseModal}
                  sx={{ borderRadius: 8, px: 3 }}
                >
                  Return to sentences
                </Button>
              </Box>
            )}
          </Box>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default CourseletSentencesView;